/**
 * Google Apps Script Form Submission Helper
 * Submits form data to Google Apps Script Web App
 */

/**
 * Submit form data to Google Apps Script
 * @param {string} formType - Type of form (e.g., 'contact_form', 'newsletter_subscription', 'catalog_request')
 * @param {Object} formData - Form data to submit
 * @returns {Promise<Object>} Response from Apps Script
 */
export const submitToAppsScript = async (formType, formData) => {
    // Get Apps Script URL from environment variable
    const APPS_SCRIPT_URL = process.env.REACT_APP_GOOGLE_APPS_SCRIPT_URL || "https://script.google.com/macros/s/AKfycbya2Oq9ZpGGX8djRY4P0yZ-5XQwCTx03AT_xKhBBpypbM0mwOChlMuEqtTTH2wOx3e7/exec";
    
    if (!APPS_SCRIPT_URL) {
        console.warn('Google Apps Script URL not configured. Skipping Apps Script submission.');
        return { success: false, error: 'Apps Script URL not configured' };
    }

    try {
        // Prepare payload for Apps Script
        const payload = {
            formType: formType,
            timestamp: new Date().toISOString(),
            ...formData
        };

        console.log('🔵 Submitting to Apps Script:', APPS_SCRIPT_URL);
        console.log('🔵 Payload:', JSON.stringify(payload, null, 2));

        // Submit to Google Apps Script using no-cors mode
        // Google Apps Script Web Apps don't support CORS preflight requests
        // Using no-cors mode allows the request to go through, but we can't read the response
        // Using FormData format works better with no-cors mode
        const formDataToSend = new FormData();
        formDataToSend.append('data', JSON.stringify(payload));

        // Alternative: Send as URL-encoded form data (more compatible)
        const urlEncodedData = new URLSearchParams();
        urlEncodedData.append('data', JSON.stringify(payload));

        const response = await fetch(APPS_SCRIPT_URL, {
            method: 'POST',
            mode: 'no-cors', // Critical: bypasses CORS restrictions
            // No custom headers allowed in no-cors mode
            body: urlEncodedData // Using URLSearchParams works with no-cors
        });

        // With no-cors mode, we can't read the response, but the request is sent
        // Apps Script will process it and save to Sheets
        console.log('✅ Apps Script request sent (no-cors mode)');
        console.log('✅ Data should be saved to Google Sheet - check the sheet to verify');
        console.log('ℹ️ Note: Cannot verify response due to no-cors mode, but request was sent successfully');
        
        // Return success since request was sent (even though we can't verify response)
        return { success: true, data: { message: 'Request sent - check Google Sheet to verify data was saved' } };

    } catch (error) {
        console.error('❌ Error submitting to Apps Script:', error);
        console.error('❌ Error details:', {
            message: error.message,
            stack: error.stack,
            name: error.name
        });
        // Don't throw error - Apps Script is secondary to FormSubmit
        return { success: false, error: error.message };
    }
};

/**
 * Submit to Apps Script AND read the response.
 *
 * The `no-cors` mode used by submitToAppsScript above makes the response
 * opaque — the browser will not let us read status or body, so the caller can
 * never tell a success from a rejection. That is tolerable for the three forms
 * where Apps Script is a secondary log, but not for the InnoTrans booking form,
 * where Mailchimp is the only destination.
 *
 * The fix is to send a "simple request", which the browser exempts from the
 * CORS preflight that Apps Script cannot answer:
 *   - Content-Type must be text/plain (NOT application/json)
 *   - no custom headers
 * Apps Script's response does carry Access-Control-Allow-Origin, so with no
 * preflight in the way the body becomes readable.
 *
 * The body is still JSON — Apps Script reads it via e.postData.contents,
 * which the existing doPost already handles.
 *
 * Requires the web app to be deployed with access set to "Anyone".
 *
 * @param {string} formType
 * @param {Object} formData
 * @returns {Promise<{success: boolean, data?: object, error?: string}>}
 */
/**
 * Turn the Apps Script `mailchimp` status into something a visitor can act on.
 *
 * The raw value is either a marker string or Mailchimp's JSON error body, e.g.
 *   mailchimp_error_400: {"title":"Invalid Resource","detail":"x@gmial.com
 *   looks fake or invalid, please enter a real email address.", ...}
 * Never show that to a customer: it is unreadable, English-only, and exposes
 * internal detail. The full value still goes to the console for debugging.
 *
 * @param {string} status
 * @returns {string} Spanish message
 */
const MAILCHIMP_MESSAGES = {
    es: {
        badEmail: 'Revisa tu dirección de email: parece contener un error de escritura.',
        noConsent: 'Debes aceptar la política de privacidad para continuar.',
        compliance: 'No podemos registrar esta dirección automáticamente. Escríbenos a marketing@hansa-flex.es y gestionamos tu cita.',
        config: 'No hemos podido completar tu solicitud. Inténtalo de nuevo o escríbenos directamente.',
        generic: 'No hemos podido enviar tu solicitud. Inténtalo de nuevo en unos minutos.',
        invalid: 'Respuesta no válida del servidor.',
    },
    en: {
        badEmail: 'Please check your email address — it appears to contain a typo.',
        noConsent: 'You must accept the privacy policy to continue.',
        compliance: 'We cannot register this address automatically. Please write to marketing@hansa-flex.es and we will arrange your meeting.',
        config: 'We could not complete your request. Please try again or contact us directly.',
        generic: 'We could not send your request. Please try again in a few minutes.',
        invalid: 'Invalid response from the server.',
    },
};

const describeMailchimpFailure = (status, lang = 'es') => {
    const raw = String(status);
    const m = MAILCHIMP_MESSAGES[lang] || MAILCHIMP_MESSAGES.es;

    if (raw.includes('looks fake or invalid')) return m.badEmail;
    if (raw.includes('skipped_no_consent')) return m.noConsent;
    // Previously unsubscribed / cleaned — a form cannot re-add them.
    if (raw.includes('compliance state') || raw.includes('Forgotten Email')) return m.compliance;
    // Configuration problem, not the visitor's fault.
    if (raw.includes('Missing Script Properties') || raw.includes('skipped')) return m.config;

    return m.generic;
};

export const submitToAppsScriptWithResponse = async (formType, formData, lang = 'es') => {
    // Dedicated URL for the InnoTrans 2026 handler (APPS_SCRIPT_INNOTRANS2026.gs),
    // which lives in a different Google account from the shared Spain form
    // handler. It MUST stay separate: that shared script has no ceo_appointment
    // branch, and pointing the other three forms at this one would silently stop
    // them logging, since this script doesn't know their form types either.
    //
    // Falls back to the shared script only so nothing crashes if the variable is
    // missing — in that case you get `mailchimp: "skipped"`, which means the URL
    // is not configured, not that Mailchimp refused.
    //
    // When the shared script is migrated later, drop REACT_APP_INNOTRANS_SCRIPT_URL
    // from .env and this falls back to it automatically.
    const APPS_SCRIPT_URL =
        process.env.REACT_APP_INNOTRANS_SCRIPT_URL ||
        process.env.REACT_APP_GOOGLE_APPS_SCRIPT_URL ||
        "https://script.google.com/macros/s/AKfycbya2Oq9ZpGGX8djRY4P0yZ-5XQwCTx03AT_xKhBBpypbM0mwOChlMuEqtTTH2wOx3e7/exec";

    const payload = {
        formType,
        timestamp: new Date().toISOString(),
        ...formData
    };

    try {
        const response = await fetch(APPS_SCRIPT_URL, {
            method: 'POST',
            // text/plain keeps this a "simple request" — no preflight.
            headers: { 'Content-Type': 'text/plain;charset=utf-8' },
            body: JSON.stringify(payload),
            redirect: 'follow'
        });

        if (!response.ok) {
            return { success: false, error: `Apps Script responded ${response.status}` };
        }

        const data = await response.json().catch(() => null);

        if (!data) {
            return { success: false, error: (MAILCHIMP_MESSAGES[lang] || MAILCHIMP_MESSAGES.es).invalid };
        }

        if (!data.success) {
            return { success: false, error: data.error || 'Apps Script reported a failure' };
        }

        // Saved to the Sheet but Mailchimp refused or was skipped — surface it
        // rather than telling the user everything worked. Translate to Spanish:
        // the raw Mailchimp payload is JSON and leaks internal detail.
        if (data.mailchimp && data.mailchimp !== 'subscribed') {
            console.error('[innotrans2026] Mailchimp:', data.mailchimp);
            return { success: false, error: describeMailchimpFailure(data.mailchimp, lang), data };
        }

        return { success: true, data };
    } catch (error) {
        return { success: false, error: error.message };
    }
};

/**
 * Submit contact form data to Apps Script
 * @param {Object} formData - Contact form data
 */
export const submitContactFormToAppsScript = async (formData) => {
    const appsScriptData = {
        department: formData.department,
        zone: formData.zone,
        name: formData.name,
        phone: formData.phone,
        email: formData.email,
        company: formData.company,
        message: formData.message,
        hasAttachment: formData.file ? 'yes' : 'no',
        attachmentName: formData.file ? formData.file.name : '',
        attachmentSize: formData.file ? formData.file.size : 0,
        privacyAccepted: formData.privacyAccepted ? 'Yes' : 'No'
    };

    return await submitToAppsScript('contact_form', appsScriptData);
};

/**
 * Submit newsletter subscription to Apps Script
 * @param {Object} formData - Newsletter form data
 */
export const submitNewsletterToAppsScript = async (formData) => {
    const appsScriptData = {
        firstName: formData.first_name,
        lastName: formData.last_name,
        email: formData.email,
        privacyAccepted: formData.accept ? 'Yes' : 'No',
        subscriptionDate: new Date().toLocaleString()
    };

    return await submitToAppsScript('newsletter_subscription', appsScriptData);
};

/**
 * Submit catalog request to Apps Script
 * @param {Object} formData - Catalog request form data
 */
export const submitCatalogRequestToAppsScript = async (formData) => {
    const appsScriptData = {
        email: formData.email,
        requestType: 'Technical Catalog',
        source: 'Exit Intent Popup',
        requestDate: new Date().toLocaleString(),
        privacyAccepted: 'Yes'
    };

    return await submitToAppsScript('catalog_request', appsScriptData);
};
